import cn from 'classnames'
import { FC, useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Checkbox } from 'core-components/library/checkbox'
import { GeoMappingWithOptions } from 'partner360/components/filters'
import { RankIcon } from 'partner360/components/partnerRanks/components/rankIcon/RankIcon'
import { compareByCoverage, PartnerSearchFilter } from 'partner360/components/partnerSearchFilter'
import { buildGeoOptionsFromPartners } from 'partner360/features/topPartnersManagement/components/topPartnerEditor/helpers/buildGeoOptionsFromPartners'
import { INCLUDE_MARKET_FLAGS } from 'partner360/features/topPartnersManagement/configs'
import { getMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/getMarketCoverage'
import { TRANSLATION_KEYS } from 'partner360/features/topPartnersManagement/translations'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import { IPartnerWithFlagRequests } from 'partner360/models/partnerFlagRequests/IPartnerWithFlagRequests'

import { RequestStatus } from '../requestStatus/RequestStatus'

import styles from './TopPartnerEditorMainInfo.module.scss'

interface IProps {
  item: any
  partnerId?: string
  partnerName: string
  partnersNotInTable: IPartnerWithFlagRequests[]
  topFlags: IP360PartnerFlags[]
  selectedCountryCodes: string[]
  onCountryCodesChange: (countryCodes: string[]) => void
  selectedPartnerIds: string[]
  onPartnerIdsChange: (partnerIds: string[]) => void
  preparedFlags: any[]
  isChangeable: boolean
  handleToggleFlag: (bitValue: number) => void
  partnerFlags: IP360PartnerFlags[]
}

export const TopPartnerEditorMainInfo: FC<IProps> = ({
  item,
  partnerId,
  partnerName,
  partnersNotInTable,
  topFlags,
  selectedCountryCodes,
  onCountryCodesChange,
  selectedPartnerIds,
  onPartnerIdsChange,
  preparedFlags,
  isChangeable,
  handleToggleFlag,
  partnerFlags,
}) => {
  const coverageFlag = topFlags[0]

  const geoOptions = useMemo(
    () => buildGeoOptionsFromPartners(partnersNotInTable),
    [partnersNotInTable]
  )

  const partnersByCountry = useMemo(() => {
    if (!selectedCountryCodes.length) {
      return partnersNotInTable
    }

    const countryCodeSet = new Set(selectedCountryCodes)
    return partnersNotInTable.filter((partner) => countryCodeSet.has(partner.CountryCode))
  }, [partnersNotInTable, selectedCountryCodes])

  const partnersWithCoverage = useMemo(() => {
    return partnersByCountry.map((partner) => ({
      ...partner,
      CoveragePercent: coverageFlag ? getMarketCoverage(partner.Coverage, coverageFlag) : null,
    }))
  }, [partnersByCountry, coverageFlag])

  return (
    <div className={styles.content}>
      <RequestStatus item={item} partnerFlags={partnerFlags} />

      <RenderWhen condition={Boolean(partnerId && partnerName)}>
        <div className={styles.partnerInfo}>
          <div className={styles.label}>
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.partnerName} />
          </div>
          <div className={styles.partnerName}>{partnerName}</div>
        </div>
      </RenderWhen>

      <RenderWhen condition={!partnerName}>
        <div className={styles.partnerInfo}>
          <div className={styles.label}>
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.country} />
          </div>
          <GeoMappingWithOptions
            options={geoOptions}
            values={selectedCountryCodes}
            onSelectChange={(_displayedIds, _allSelectedIds, onlyLeavesIds) =>
              onCountryCodesChange(onlyLeavesIds)
            }
            typeOfSelectedNodes="onlyLeavesItems"
          />
        </div>

        <div className={styles.partnerInfo}>
          <div className={styles.label}>
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.partnerName} />
          </div>
          <PartnerSearchFilter
            value={selectedPartnerIds}
            partners={partnersWithCoverage}
            onChange={onPartnerIdsChange}
            sortComparator={compareByCoverage}
            partnerNameClassName={styles.partnerNameInSelect}
            disabled={!selectedCountryCodes.length}
          />
        </div>
      </RenderWhen>

      <RenderWhen condition={INCLUDE_MARKET_FLAGS}>
        <div className={styles.section}>
          <div className={styles.label}>
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.flags} />
          </div>

          <div className={styles.flagsList}>
            {preparedFlags.map((flag) => (
              <button
                disabled={!isChangeable}
                key={flag.BitValue}
                type="button"
                className={cn(styles.flagCard, {
                  [styles.flagCardSelected]: flag.checked,
                })}
                onClick={() => handleToggleFlag(flag.BitValue)}
              >
                <div className={styles.checkboxWrapper} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={flag.checked}
                    onChange={() => handleToggleFlag(flag.BitValue)}
                    disabled={!isChangeable}
                  />
                </div>

                <RankIcon partnerFlag={flag.PartnerFlag} rankConfig={flag} />

                <div className={styles.flagTitle}>{flag.Title}</div>
              </button>
            ))}
          </div>
        </div>
      </RenderWhen>
    </div>
  )
}
